import { useTable } from "react-table";
import { useContext, useMemo, useState } from "react";
import {
  formatCurrency,
  getPlatform,
  getStoreNames,
  truncateString,
} from "../../../helpers";
import primaryComponents from "../../primaryComponents";
import secondaryComponents from "../../secondaryComponents";
import { useNavigate } from "react-router-dom";
import { platformHandler } from "../../../hooks/context";
import { useSelector } from "react-redux";
import { DeleteIcon } from "../../../assets/svgIcons";

// own component so the columns memo doesn't depend on storeList; a new
// Cell function would re-mount every cell and drop the hovered tooltip
const StoresCell = ({ cell: { value } }) => {
  const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const storeNames = getStoreNames(value, storeList);
  if (!storeNames) return "N/A";
  return (
    <secondaryComponents.ConcatenateWithTooltip
      value={storeNames}
      noOfCharacters={30}
    />
  );
};

const useSuppliersTable = ({ suppliers }) => {
  const [isDeleteSupplier, setIsDeleteSupplier] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState({});
  const [isEditSupplier, setIsEditSupplier] = useState(false);
  const [isUpdateCredit, setIsUpdateCredit] = useState(false);
  const navigate = useNavigate();
  const { platform } = useContext(platformHandler);
  const { currency_code } = useSelector(
    (state) => state.profileDetailsReducer.businessDetails,
  );

  const columns = useMemo(
    () => [
      {
        Header: "Supplier Name",
        accessor: "name",
        Cell: ({ row, cell: { value } }) => truncateString(value, 200),
      },

      {
        Header: "Credit Till Date",
        accessor: "total_credit",
        Cell: ({ cell: { value } }) =>
          formatCurrency({ value: value ?? 0, currencyCode: currency_code }),
      },
      {
        Header: "Repayment",
        accessor: "total_repayment",
        Cell: ({ cell: { value } }) =>
          formatCurrency({ value: value ?? 0, currencyCode: currency_code }),
      },
      {
        Header: "Outstanding Credit",
        accessor: "total_outstanding_balance",
        Cell: ({ cell: { value } }) => (
          <span className={value === 0 ? "text-success" : "text-danger"}>
            {formatCurrency({ value: value ?? 0, currencyCode: currency_code })}
          </span>
        ),
      },
      {
        Header: "Stores",
        accessor: "stores",
        Cell: StoresCell,
      },
      {
        Header: "Action",
        Cell: ({ row }) => (
          <span className="d-flex align-items-center" style={{ gap: "10px" }}>
            <primaryComponents.Button
              onClick={() =>
                navigate(
                  `${getPlatform(platform)}/inventory/suppliers/${
                    row?.original?.id
                  }`,
                )
              }
              classNames={"btn btn--primary smallBtn"}
            >
              View Invoices
            </primaryComponents.Button>
            <secondaryComponents.NavigateWithPermission
              permissionParentKey={"Inventory Management"}
              permissionChildKey="can_view_edit_and_create_suppliers"
            >
              <primaryComponents.Button
                onClick={() => {
                  setIsEditSupplier(true);
                  setSelectedSupplier(row?.original);
                }}
                classNames="btn smallBtn btn--outline"
                style={{
                  fontSize: "12px",
                  width: "100px",
                }}
              >
                Edit Supplier
              </primaryComponents.Button>
            </secondaryComponents.NavigateWithPermission>
            <secondaryComponents.NavigateWithPermission
              permissionParentKey={"Inventory Management"}
              permissionChildKey="can_view_edit_and_create_suppliers"
            >
              <secondaryComponents.ToolTipV2 tipText="Delete Record">
                <span
                  className="cursor-pointer"
                  onClick={() => {
                    setIsDeleteSupplier(true);
                    setSelectedSupplier(row?.original);
                  }}
                >
                  <DeleteIcon />
                </span>
              </secondaryComponents.ToolTipV2>
            </secondaryComponents.NavigateWithPermission>
          </span>
        ),
      },
    ],
    [],
  );

  const instance = useTable({ columns, data: suppliers });
  return {
    instance,
    isDeleteSupplier,
    selectedSupplier,
    isEditSupplier,
    isUpdateCredit,
    setIsDeleteSupplier,
    setIsEditSupplier,
    setIsUpdateCredit,
  };
};

export default useSuppliersTable;
